import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  Edit,
  Facebook,
  Instagram,
  X as XIcon,
} from '@mui/icons-material';
import { fetchAuthors } from "./../../actions/authorsActions";
import LoadingBackdrop from "../Common/LoadingBackdrop";

const AuthorsTable = ({ searchText = "" }) => {
  const dispatch = useDispatch();
  const authors = useSelector((state) => state.authors.authors);
  const loading = useSelector((state) => state.authors.isLoading);

  useEffect(() => {
    dispatch(fetchAuthors(3));
  }, [dispatch]);

  const filteredAuthors = authors?.filter(author =>
    author.author_name.toLowerCase().includes(searchText.toLowerCase())
  );

  if (loading && !authors) {
    return <LoadingBackdrop />;
  }

  return (
    <Box sx={{ py: 2 }}>
      <TableContainer
        component={Paper}
        sx={{
          marginTop: 3,
          boxShadow: 2,
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ width: '100px' }}><strong>Picture</strong></TableCell>
              <TableCell><strong>Author Name</strong></TableCell>
              <TableCell align="right" sx={{ width: '200px' }}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAuthors?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {searchText ? "No authors found matching your search" : "No authors available"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAuthors?.map((author) => (
                <TableRow
                  key={author.author_id + author.author_name}
                  sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                >
                  <TableCell sx={{ width: '100px' }}>
                    {author.author_image && (
                      <Box
                        component="img"
                        src={author.author_image}
                        alt={author.author_name}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="subtitle1">{author.author_name}</Typography>
                      {author.author_title && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {author.author_title}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {author.author_num_sessions || 0} {author.author_num_sessions === 1 ? 'session' : 'sessions'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ width: '200px' }}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      {author.author_facebook_url && (
                        <Tooltip title="Facebook">
                          <IconButton
                            href={author.author_facebook_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                          >
                            <Facebook />
                          </IconButton>
                        </Tooltip>
                      )}
                      {author.author_instagram_url && (
                        <Tooltip title="Instagram">
                          <IconButton
                            href={author.author_instagram_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                          >
                            <Instagram />
                          </IconButton>
                        </Tooltip>
                      )}
                      {author.author_twitter_url && (
                        <Tooltip title="X">
                          <IconButton
                            href={author.author_twitter_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                          >
                            <XIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton
                          component={Link}
                          to={`/authors/edit/${author.author_id}`}
                          color="info"
                          sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AuthorsTable;
